import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ControleDetail } from "@/components/ControleDetail";
import { api, errMsg, money } from "@/lib/api";

const Th = ({ children, right }) => <th className={`py-1.5 px-2 text-[11px] font-mono uppercase tracking-wider text-slate-500 ${right ? "text-right" : "text-left"}`}>{children}</th>;
const DETAIL = { "Nuits réservées": "nuits", "Taxes remises par Airbnb": "th" };
const val = (mesure, v) => (typeof v !== "number" ? v : mesure === "Nuits réservées" ? v : money(v));

const EcartRow = ({ e, idx, i, busy, onApply }) => (
  <tr className="border-t border-slate-100" data-testid={`controle-booking-row-${idx}-${i}`}>
    <td className="py-1.5 px-2 font-mono">{e.code}</td>
    <td className="px-2">{e.nom_client || "—"}</td>
    <td className="px-2 text-slate-600">{e.logement}</td>
    <td className="px-2">{e.champ}</td>
    <td className="px-2 text-right tabular">{val(e.champ, e.application)}</td>
    <td className="px-2 text-right tabular text-red-600 font-semibold">{val(e.champ, e.booking)}</td>
    <td className="px-2 text-right">{e.champ === "Commission" && (
      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={busy} onClick={() => onApply([e.code])} data-testid={`controle-appliquer-${idx}-${i}`}>
        Appliquer la valeur Booking
      </Button>
    )}</td>
  </tr>
);

const EcartsBooking = ({ ecarts, idx, importId, onChange }) => {
  const [busy, setBusy] = useState(false);
  const commissions = ecarts.filter((e) => e.champ === "Commission").map((e) => e.code);
  const apply = async (codes) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/imports/${importId}/appliquer-commissions`, { codes });
      toast.success(`${data.appliquees} commission(s) corrigée(s) avec la valeur Booking`);
      onChange(data.ecarts);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      {commissions.length > 1 && (
        <Button size="sm" className="mb-2 bg-emerald-700 hover:bg-emerald-800" disabled={busy} onClick={() => apply(commissions)} data-testid={`controle-appliquer-tout-${idx}`}>
          {busy && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}Tout appliquer ({commissions.length} commissions)
        </Button>
      )}
      <table className="w-full text-xs" data-testid={`controle-booking-table-${idx}`}>
        <thead><tr><Th>Réservation</Th><Th>Client</Th><Th>Logement</Th><Th>Champ</Th><Th right>Application</Th><Th right>Booking</Th><Th /></tr></thead>
        <tbody>{ecarts.map((e, i) => <EcartRow key={`${e.code}-${e.champ}`} e={e} idx={idx} i={i} busy={busy} onApply={apply} />)}</tbody>
      </table>
    </>
  );
};

const ControleRow = ({ l, idx, i, onDetail, ouvert }) => {
  const ko = Math.abs(l.ecart) > 0.005;
  return (
    <tr className={`border-t border-slate-100 ${ko ? "bg-red-50/60" : ""}`} data-testid={`controle-airbnb-row-${idx}-${i}`}>
      <td className="py-1.5 px-2 text-slate-500">{l.groupe}</td>
      <td className="px-2">{l.libelle}</td>
      <td className="px-2">{l.mesure}</td>
      <td className="px-2 text-right tabular">{val(l.mesure, l.airbnb)}</td>
      <td className="px-2 text-right tabular">{val(l.mesure, l.application)}</td>
      <td className={`px-2 text-right tabular font-semibold ${ko ? "text-red-600" : "text-emerald-700"}`}>{ko ? val(l.mesure, l.ecart) : "✓"}</td>
      <td className="px-2 text-right">{ko && l.groupe === "Logement" && DETAIL[l.mesure] && (
        <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => onDetail(l)} data-testid={`controle-detail-btn-${idx}-${i}`}>{ouvert ? "Masquer le détail" : "Voir le détail"}</Button>
      )}</td>
    </tr>
  );
};

const ControleAirbnb = ({ c, idx, importId }) => {
  const [detail, setDetail] = useState(null);
  return (
    <>
      <p className="text-xs text-slate-600 mb-2" data-testid={`controle-airbnb-resume-${idx}`}>
        Période du {c.debut} au {c.fin} · {c.reservations} réservation(s) Airbnb comparée(s) (date de versement) ·{" "}
        <b className={c.nb_ecarts ? "text-red-600" : "text-emerald-700"}>{c.nb_ecarts ? `${c.nb_ecarts} écart(s)` : "aucun écart"}</b>
      </p>
      <table className="w-full text-xs" data-testid={`controle-airbnb-table-${idx}`}>
        <thead><tr><Th>Niveau</Th><Th>Libellé</Th><Th>Mesure</Th><Th right>Airbnb</Th><Th right>Application</Th><Th right>Écart</Th><Th /></tr></thead>
        <tbody>{c.lignes.map((l, i) => <ControleRow key={`${l.groupe}-${l.libelle}-${l.mesure}`} l={l} idx={idx} i={i} ouvert={detail === l} onDetail={(x) => setDetail(detail === x ? null : x)} />)}</tbody>
      </table>
      {detail && <ControleDetail importId={importId} logement={detail.libelle} mesure={DETAIL[detail.mesure]} />}
    </>
  );
};

const Resultat = ({ r, idx, onChange }) => {
  const [ecarts, setEcarts] = useState(r.ecarts || []);
  const importId = r.import_id || r.id;
  const maj = (e) => { setEcarts(e); onChange?.(); };
  return (
    <section className="rounded-md border border-slate-200 p-3" data-testid={`controle-resultat-${idx}`}>
      <h4 className="text-sm font-semibold truncate">{r.fichier}</h4>
      {r.controle ? <div className="mt-2 overflow-x-auto"><ControleAirbnb c={r.controle} idx={idx} importId={importId} /></div> : (
        <>
          <p className="mt-1 text-xs text-slate-600">
            {r.inserted ?? 0} réservation(s) ajoutée(s){r.annulees ? ` (dont ${r.annulees} annulée(s))` : ""} · {r.doublons ?? 0} déjà importée(s), nom du client et date de réservation complétés ·{" "}
            <b className={ecarts.length ? "text-red-600" : "text-emerald-700"} data-testid={`controle-ecarts-compte-${idx}`}>{ecarts.length ? `${ecarts.length} écart(s) à vérifier` : "aucun écart"}</b>
          </p>
          {ecarts.length > 0 && <div className="mt-2 overflow-x-auto"><EcartsBooking ecarts={ecarts} idx={idx} importId={importId} onChange={maj} /></div>}
        </>
      )}
    </section>
  );
};

export const aControler = (d) => !!(d?.controle || d?.ecarts?.length || d?.annulees || d?.confirmees);

export const ImportControleDialog = ({ resultats, onClose, onChange }) => (
  <Dialog open={!!resultats?.length} onOpenChange={(o) => { if (!o) onClose(); }}>
    <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="import-controle-dialog">
      <DialogHeader>
        <DialogTitle>Contrôle des fichiers importés</DialogTitle>
        <DialogDescription>Comparaison entre les relevés des plateformes et les données de l'application.</DialogDescription>
      </DialogHeader>
      <div className="space-y-3">{resultats?.map((r, i) => <Resultat key={`${r.fichier}-${i}`} r={r} idx={i} onChange={onChange} />)}</div>
    </DialogContent>
  </Dialog>
);
